import { useContext, useEffect, useState } from "react";
import { generateAuthHeader } from "../../../commonfunctions/authentication";
import { log } from "../../../commonfunctions/logger";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import PDFDownloadButton from "../../../components/PDFDownloadButton/PDFDownloadButton";
import CreditNotePDF from "../../../components/PDFTemplates/Templates/TransactionPDF/CreditNotePDF";
import InvoicePDF from "../../../components/PDFTemplates/Templates/TransactionPDF/InvoicePDF";
import { TransactionDetails, TransactionSummary } from "../../../interfaces/interfaces";
import { Context, IContext } from "../../../context/ContextApp";
import Button, { ButtonType } from "../../../components/Button/Button";
import Icon from "../../../components/Icon/icon";
import StatusBadge from "../../../components/StatusBadge/StatusBadge";

interface Props {
  transaction: TransactionSummary;
}

const TransactionCard = ({ transaction }: Props) => {
  const [isPrintable, setIsPrintable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPrintPopup, setShowPrintPopup] = useState<boolean>(false);
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetails | null>(null);
  const { appBranch }: IContext = useContext(Context);

  useEffect(() => {
    if (transaction.type === "Invoice" || transaction.type === "CreditNote") setIsPrintable(true);
  }, [transaction.type]);

  const getTransactionDetail = async (t: TransactionSummary) => {
    setIsLoading(true);
    let url = `${process.env.REACT_APP_HALFBACK_ROOT_API}/current_customer/transactions/`;
    if (t.type === "Invoice") url += `invoice_detail?invoiceNo=${t.transactionNumber}`;
    if (t.type === "CreditNote") url += `credit_note_detail?creditNoteNo=${t.transactionNumber}`;

    try {
      const res = await fetch(url, generateAuthHeader());

      if (res.ok) {
        const data = await res.json();
        setTransactionDetails(data);
      } else {
        const error = await res.text();
        throw error;
      }
    } catch (error) {
      log(error, "Failed to load transaction details");
    } finally {
      setIsLoading(false);
    }
  };

  function renderPrinterPopup() {
    if (!showPrintPopup) return;

    if (isLoading) {
      return (
        <div className="popup-modal">
          <div className="popup-modal-content popup-modal-content--loading">
            <div className="popup-modal-spinner-wrapper">
              <LoadingSpinner inline />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="popup-modal">
          <div className="popup-modal-content">
            {transaction.type === "CreditNote" ? (
              <div>
                <h2>Credit Note</h2>
                <p>
                  Transaction No. <b>{transaction.transactionNumber}</b>
                </p>
                <p>
                  Order No. <b>{transaction.orderNo}</b>
                </p>
              </div>
            ) : (
              <div>
                <h2>Invoice</h2>
                <p>
                  Transaction No. <b>{transaction.transactionNumber}</b>
                </p>
                <p>
                  Order No. <b>{transaction.orderNo}</b>
                </p>
              </div>
            )}

            <div className="popup-modal-buttons">
              <PDFDownloadButton
                document={
                  transaction.type === "Invoice" ? (
                    <InvoicePDF transactionDetails={transactionDetails} appBranch={appBranch} />
                  ) : (
                    <CreditNotePDF transactionDetails={transactionDetails} appBranch={appBranch} />
                  )
                }
              >
                <Button type={ButtonType.primary}>
                  <Icon
                    name="ExternalLink"
                    iconWidth="1rem"
                    className="icon--inherit"
                    style={{ marginRight: "0.5em" }}
                  />
                  View
                </Button>
              </PDFDownloadButton>
              <Button
                type={ButtonType.secondary}
                onClick={() => {
                  setShowPrintPopup(false);
                  setTransactionDetails(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      );
    }
  }

  function handleOnClick() {
    setShowPrintPopup(true);
    getTransactionDetail(transaction);
  }

  return (
    <>
      <td className="transaction-cell--number" data-label="Transaction No.">
        {transaction.transactionNumber}
      </td>
      <td className="transaction-cell--center" data-label="Type">
        {transaction.type === "CreditNote" ? "Credit Note" : transaction.type}
      </td>
      <td className="transaction-cell--center" data-label="Date">
        {transaction.date}
      </td>
      <td className="transaction-cell--center transaction-cell--status" data-label="Status">
        <StatusBadge status={transaction.status} />
      </td>
      <td className="transaction-cell--left" data-label="Order">
        {transaction.orderNo}
      </td>
      <td className="transaction-cell--right" data-label="Debit">
        {transaction.debit || "—"}
      </td>
      <td className="transaction-cell--right transaction-cell--actions" data-label="Actions">
        {isPrintable ? (
          <Button
            type={ButtonType.secondary}
            onClick={handleOnClick}
            disabled={isLoading}
            isLoading={isLoading}
            className="transaction-view-button"
          >
            <Icon
              name="Print"
              iconWidth="1rem"
              className="icon--black"
              style={{ marginRight: "0.5em" }}
            />
            View {transaction.type === "CreditNote" ? "Credit Note" : "Invoice"}
          </Button>
        ) : (
          <span className="transaction-no-action">—</span>
        )}
      </td>

      {renderPrinterPopup()}
    </>
  );
};

export default TransactionCard;
